import React from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Toolbar,
  AppBar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import PropTypes from "prop-types";

/**
 * MBDrawer - A flexible drawer component that supports both regular and full-screen modes
 *
 * Features:
 * - Regular drawer mode with customizable height (when opening from bottom)
 * - Full-screen mode for maximum content space
 * - Responsive design (mobile-friendly)
 * - Customizable header with title and close button
 * - Smooth animations and Material-UI integration
 * - Opens from bottom by default for better mobile experience
 *
 * Usage:
 * <MBDrawer
 *   open={isOpen}
 *   onClose={handleClose}
 *   title="My Drawer"
 *   fullScreen={true}
 * >
 *   <YourContent />
 * </MBDrawer>
 */

const MBDrawer = ({
  open,
  onClose,
  title,
  children,
  fullScreen = false,
  anchor = "bottom",
  height = 400,
  width = 400,
  ...drawerProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // For full screen mode, use a different approach
  if (fullScreen) {
    return (
      <Drawer
        anchor={anchor}
        open={open}
        onClose={onClose}
        variant="temporary"
        PaperProps={{
          sx: {
            width: "100vw",
            height: "98vh",
            maxWidth: "100vw",
            maxHeight: "98vh",
            margin: "auto",
            borderTopLeftRadius: anchor === "bottom" ? 8 : 0,
            borderTopRightRadius: anchor === "bottom" ? 8 : 0,
            borderBottomLeftRadius: anchor === "bottom" ? 0 : 8,
            borderBottomRightRadius: anchor === "bottom" ? 0 : 8,
          },
        }}
        ModalProps={{
          keepMounted: false,
        }}
        {...drawerProps}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Header with close button */}
          <AppBar
            position="static"
            color="primary"
            elevation={1}
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: "bold" }}>
                {title}
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="close"
                sx={{
                  marginLeft: 2,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <Close />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Content area */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              backgroundColor: theme.palette.background.default,
            }}
          >
            {children}
          </Box>
        </Box>
      </Drawer>
    );
  }

  // Regular drawer mode - optimized for bottom opening
  let borderTopLeftRadius = theme.spacing(1);
  let borderTopRightRadius = theme.spacing(1);
  let borderBottomLeftRadius = theme.spacing(1);
  let borderBottomRightRadius = theme.spacing(1);
  if (anchor === "bottom") {
    borderTopLeftRadius = 0;
    borderTopRightRadius = 0;
  }
  if (anchor === "top") {
    borderBottomLeftRadius = 0;
    borderBottomRightRadius = 0;
  }
  if (anchor === "left" || anchor === "right") {
    borderBottomLeftRadius = 0;
    borderBottomRightRadius = 0;
    borderTopLeftRadius = 0;
    borderTopRightRadius = 0;
  }

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      variant="temporary"
      PaperProps={{
        sx: {
          width: isMobile ? "100vw" : width,
          height: isMobile ? "100vh" : height,
          maxHeight: "100vh",
          borderTopLeftRadius: borderTopLeftRadius,
          borderTopRightRadius: borderTopRightRadius,
          borderBottomLeftRadius: borderBottomLeftRadius,
          borderBottomRightRadius: borderBottomRightRadius,
          margin: "auto",
        },
      }}
      ModalProps={{
        keepMounted: false,
      }}
      {...drawerProps}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header with drag handle for bottom drawer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            position: "relative",
          }}
        >
          {/* Drag handle for bottom drawer */}
          {anchor === "bottom" && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: "50%",
                transform: "translateX(-50%)",
                width: 40,
                height: 4,
                backgroundColor: theme.palette.grey[400],
                borderRadius: 2,
              }}
            />
          )}

          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: "bold",
              marginTop: anchor === "bottom" ? 2 : 0,
              flex: 1,
            }}
          >
            {title}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close" size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
          }}
        >
          {children}
        </Box>
      </Box>
    </Drawer>
  );
};

MBDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  fullScreen: PropTypes.bool,
  anchor: PropTypes.oneOf(["left", "top", "right", "bottom"]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MBDrawer.defaultProps = {
  title: "",
  children: null,
  fullScreen: false,
  anchor: "bottom",
  height: 400,
  width: 400,
};

export default MBDrawer;
